import React, { useState, useEffect, useCallback } from "react";
import { Layers, AlertTriangle, Check, Wand2 } from "lucide-react";
import { eur } from "../igualdad-data.js";
import { apiGet, apiPut, apiGetAll } from "../igualdad-api.js";
import { useIgualdad } from "../igualdad-store.jsx";
import { Status, SectionHead } from "../igualdad-components.jsx";

/* ============================================================
   Talention — Módulo Igualdad · Agrupar conceptos en familias
   Vista de trabajo, no CRUD: el catálogo de tipos de concepto
   sirve para mantenerlos, pero para decidir agrupaciones hace
   falta ver cada concepto con su masa salarial, cuánta gente lo
   cobra y cómo se equipara.
   Criterio: dos conceptos van a la misma familia exactamente
   cuando se equiparan igual. La herramienta propone; decide
   quien la usa.
   ============================================================ */

// Etiquetas legibles de la firma de equiparación.
const CRITERIOS = [
  ["normalizable", "Normaliza por jornada"],
  ["anualizable", "Eleva al periodo"],
  ["computa_total", "Computa al total"],
];

// Bloques del registro oficial: el Ministerio reserva una tanda de columnas
// para cada uno, de modo que dos conceptos de bloques distintos no comparten
// columna por mucho que se equiparen igual.
const BLOQUES = {
  salario_base: "Salario base",
  complemento_salarial: "Complemento salarial",
  extrasalarial: "Extrasalarial",
};

function firmaLegible(criterios) {
  if (!criterios) return "—";
  const activos = CRITERIOS.filter(([k]) => criterios[k]).map(([, l]) => l);
  const naturaleza = criterios.naturaleza === "especie" ? "En especie" : "En efectivo";
  const bloque = BLOQUES[criterios.bloque];
  return [...(bloque ? [bloque] : []), naturaleza, ...(activos.length ? activos : ["sin ajustes"])].join(" · ");
}

/* Marcas de los cuatro criterios de un concepto, para poder comparar de un
   vistazo dos conceptos que se parecen en el nombre. */
function Criterios({ c }) {
  const marcas = [
    [c.naturaleza === "especie" ? "especie" : "efectivo", true],
    ["normaliza", c.normalizable],
    ["eleva", c.anualizable],
    ["computa", c.computa_total],
  ];
  return (
    <span className="rb-crit">
      {marcas.map(([txt, on], i) => (
        <span key={i} className={"rb-crit-i" + (on ? " on" : "")}>{txt}</span>
      ))}
    </span>
  );
}

function IgualdadAgrupacion() {
  const { session, fireToast } = useIgualdad();
  const politicaId = session.politicaId;
  const clienteId = session.clienteId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [datos, setDatos] = useState(null);
  const [familias, setFamilias] = useState([]);

  const [seleccion, setSeleccion] = useState([]);
  const [destino, setDestino] = useState("");
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    if (!politicaId) { setLoading(false); setDatos(null); return; }
    setLoading(true); setError(null);
    try {
      const res = await apiGet("/agrupacion-conceptos", { politica_id: politicaId });
      setDatos(res.data);
      setSeleccion([]);
    } catch (e) {
      setError(e.message || true);
    } finally {
      setLoading(false);
    }
  }, [politicaId]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      if (!clienteId) { setFamilias([]); return; }
      try {
        const lista = await apiGetAll("/familias-concepto", { cliente_id: clienteId });
        if (vivo) setFamilias(lista || []);
      } catch { if (vivo) setFamilias([]); }
    })();
    return () => { vivo = false; };
  }, [clienteId]);

  function alternar(id) {
    setSeleccion((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  }

  /* Carga la selección con los conceptos de una propuesta y preselecciona la
     familia que ya use la mayoría, si alguna. */
  function tomarPropuesta(p) {
    setSeleccion(p.conceptos.map((c) => c.id));
    setDestino(p.familia_sugerida ? String(p.familia_sugerida.id) : "");
    fireToast(`${p.n_conceptos} conceptos seleccionados. Elige la familia y confirma.`);
  }

  async function asignar(familiaId) {
    if (!seleccion.length || guardando) return;
    setGuardando(true);
    try {
      const res = await apiPut("/agrupacion-conceptos", {
        politica_id: politicaId,
        conceptos: seleccion,
        familia_id: familiaId,
      });
      fireToast(`${res.data.modificados} concepto(s) actualizados.`);
      setDestino("");
      await cargar();
    } catch (e) {
      fireToast(e.message || "No se pudo guardar la agrupación.");
    } finally {
      setGuardando(false);
    }
  }

  if (loading || error || !datos) {
    return (
      <Status
        loading={loading}
        error={error}
        empty={!loading && !error && !datos}
        emptyText="Selecciona una política retributiva para agrupar sus conceptos."
      />
    );
  }

  const { resumen, conceptos, propuestas, incoherencias } = datos;
  const ahorro = resumen.n_conceptos - resumen.n_decisiones_equiparacion;

  return (
    <div>
      {/* Resumen: la cifra que justifica agrupar. */}
      <div className="rb-kpis rb-kpis-4">
        <div className="rb-kpi">
          <div className="rb-kpi-l">Conceptos</div>
          <div className="rb-kpi-v">{resumen.n_conceptos}</div>
          <div className="rb-kpi-s">{resumen.n_sin_familia} sin familia</div>
        </div>
        <div className="rb-kpi">
          <div className="rb-kpi-l">Decisiones de equiparación</div>
          <div className="rb-kpi-v">{resumen.n_decisiones_equiparacion}</div>
          <div className="rb-kpi-s">formas distintas de tratar un importe</div>
        </div>
        <div className="rb-kpi">
          <div className="rb-kpi-l">Familias en uso</div>
          <div className="rb-kpi-v">{resumen.n_familias_usadas}</div>
          <div className="rb-kpi-s">de {familias.length} en el catálogo</div>
        </div>
        <div className="rb-kpi">
          <div className="rb-kpi-l">Masa retributiva</div>
          <div className="rb-kpi-v">{eur(resumen.masa_total)}</div>
          <div className="rb-kpi-s">{eur(resumen.masa_sin_familia)} sin agrupar</div>
        </div>
      </div>

      {ahorro > 0 && (
        <div className="rb-banner rb-banner-info">
          <Layers size={16} />
          <span>
            <b>{resumen.n_conceptos} conceptos, {resumen.n_decisiones_equiparacion} decisiones reales.</b>{" "}
            Los criterios de equiparación se mantienen hoy concepto a concepto. Agrupándolos en familias,
            esas decisiones pasan de {resumen.n_conceptos} a {resumen.n_decisiones_equiparacion}.
          </span>
        </div>
      )}

      {/* Incoherencias: familias que no cumplen lo que prometen. */}
      {incoherencias.length > 0 && (
        <div className="rb-card">
          <SectionHead icon={AlertTriangle} title={`Familias cuyos conceptos no se equiparan igual · ${incoherencias.length}`} />
          <p className="rb-note">
            Una familia promete que sus conceptos se tratan igual. Aquí no se cumple, así que agrupan
            cosas que el cálculo trata distinto. Conviene revisarlas antes de seguir agrupando.
          </p>
          {incoherencias.map((h) => (
            <div key={h.familia_id} className="rb-inco">
              <div className="rb-inco-h">
                <b>{h.familia_nombre}</b>
                <span className="rb-soft">{h.n_firmas} tratamientos distintos · {eur(h.masa)}</span>
              </div>
              <div className="rb-inco-b">
                {h.conceptos.map((c) => (
                  <span key={c.id} className="rb-inco-c">{c.nombre} <Criterios c={c} /></span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Propuestas: conceptos que se equiparan igual y no comparten familia. */}
      <div className="rb-card">
        <SectionHead icon={Wand2} title={`Candidatos a agrupar · ${propuestas.length}`} />
        {propuestas.length === 0 ? (
          <p className="rb-note">
            No hay conceptos sueltos que se equiparen igual entre sí. Nada que proponer.
          </p>
        ) : (
          <>
            <p className="rb-note">
              Conceptos que hoy están separados y que el cálculo trata exactamente igual.
              Son candidatos, no una decisión: revísalos antes de agrupar.
            </p>
            {propuestas.map((p) => (
              <div key={p.firma_equiparacion} className="rb-prop">
                <div className="rb-prop-h">
                  <div>
                    <b>{p.n_conceptos} conceptos</b>{" "}
                    <span className="rb-soft">· {firmaLegible(p.criterios)}</span>
                  </div>
                  <div>
                    <span className="rb-mono" style={{ marginRight: 10 }}>{eur(p.masa)}</span>
                    <button className="rb-btn-soft rb-btn-sm" onClick={() => tomarPropuesta(p)}>
                      Seleccionar
                    </button>
                  </div>
                </div>
                <div className="rb-prop-b">
                  {p.conceptos.map((c) => (
                    <span key={c.id} className="rb-srcpill">{c.nombre}</span>
                  ))}
                </div>
                {p.familia_sugerida && (
                  <p className="rb-note" style={{ marginTop: 6 }}>
                    {p.familia_sugerida.n_conceptos_ya_asignados} de ellos ya están en{" "}
                    <b>{p.familia_sugerida.nombre}</b>.
                  </p>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Catálogo completo con la evidencia para decidir. */}
      <div className="rb-card rb-card-flat">
        <div style={{ padding: "14px 16px" }}>
          <SectionHead icon={Layers} title="Conceptos de la política" />
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="rb-table">
            <thead>
              <tr>
                <th style={{ width: 34 }} />
                <th className="l">Concepto</th>
                <th className="l">Familia</th>
                <th className="l">Equiparación</th>
                <th>Personas</th>
                <th>Masa</th>
                <th>% masa</th>
              </tr>
            </thead>
            <tbody>
              {conceptos.map((c) => (
                <tr key={c.id} className={seleccion.includes(c.id) ? "is-sel" : ""}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Seleccionar ${c.nombre}`}
                      checked={seleccion.includes(c.id)}
                      onChange={() => alternar(c.id)}
                    />
                  </td>
                  <td className="l"><b>{c.nombre}</b></td>
                  <td className="l">
                    {c.familia_nombre
                      ? <span className="rb-chip rb-chip-ok">{c.familia_nombre}</span>
                      : <span className="rb-soft">sin familia</span>}
                  </td>
                  <td className="l"><Criterios c={c} /></td>
                  <td className="rb-mono">{c.personas}</td>
                  <td className="rb-mono">{eur(c.masa)}</td>
                  <td className="rb-mono">{`${c.pct_masa}%`}</td>
                </tr>
              ))}
              {conceptos.length === 0 && (
                <tr><td className="l rb-soft" colSpan={7}>Esta política no tiene conceptos.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Barra de acción: solo aparece con algo seleccionado. */}
        {seleccion.length > 0 && (
          <div className="rb-tablefoot rb-acciones">
            <span><b>{seleccion.length}</b> concepto(s) seleccionados</span>
            <div className="rb-acciones-r">
              <select
                className="rb-inp"
                aria-label="Familia de destino"
                value={destino}
                onChange={(e) => setDestino(e.target.value)}
              >
                <option value="">Elegir familia…</option>
                {familias.map((f) => (
                  <option key={f.id} value={f.id}>{f.nombre}</option>
                ))}
              </select>
              <button
                className="rb-btn"
                disabled={!destino || guardando}
                onClick={() => asignar(Number(destino))}
              >
                <Check size={14} /> {guardando ? "Guardando…" : "Agrupar"}
              </button>
              <button
                className="rb-btn-ghost"
                disabled={guardando}
                onClick={() => asignar(null)}
              >
                Quitar familia
              </button>
              <button className="rb-btn-ghost" onClick={() => setSeleccion([])}>Cancelar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export { IgualdadAgrupacion, firmaLegible };
